export interface BoxSize {
  width: number;
  height: number;
}

export type SnapZone =
  'left' | 'right' | 'top' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null;

export interface SnapBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(v, hi));

/** zone 이 요구하는 타일 기하 — min 을 아직 반영하지 않은 순수 분할. */
function zoneRect(zone: SnapZone, pw: number, ph: number): SnapBounds | null {
  const halfW = Math.round(pw / 2);
  const halfH = Math.round(ph / 2);
  switch (zone) {
    case 'left':
      return { x: 0, y: 0, width: halfW, height: ph };
    case 'right':
      return { x: halfW, y: 0, width: pw - halfW, height: ph };
    case 'top':
      return { x: 0, y: 0, width: pw, height: ph };
    case 'top-left':
      return { x: 0, y: 0, width: halfW, height: halfH };
    case 'top-right':
      return { x: halfW, y: 0, width: pw - halfW, height: halfH };
    case 'bottom-left':
      return { x: 0, y: halfH, width: halfW, height: ph - halfH };
    case 'bottom-right':
      return { x: halfW, y: halfH, width: pw - halfW, height: ph - halfH };
    case null:
    default:
      return null;
  }
}

/**
 * 스냅 zone 기하를 윈도우 min 사이즈로 바닥을 잡아 해결한다.
 *
 * 왜 필요한가 — 타일 크기를 그대로 적용하면 대각(4분면) 스냅이 부모의 1/4 지만,
 * re-resizable 은 `minWidth/minHeight` 를 **인라인 CSS min-width/min-height** 로 걸어두므로
 * 박스는 타일까지 줄지 않는다. 즉 위치만 4분면 앵커로 가고 크기는 min 그대로 남아
 * 창이 부모 밖으로 삐져나가고, 조상의 `overflow:hidden` 이 그 부분을 잘라낸다.
 * 실측(1400×900 평면 · standard 프리셋 1000×600, 우하단 스냅): 위치 (700,450) 에 크기
 * 1000×600 → 우측 300px · 하단 150px 이 평면 밖. 스냅이 자신이 만든 크기를 모른 채
 * 좌표를 잡은 것이 원인이다.
 *
 * - min 이 타일보다 크면 그 축을 min 까지 넓혀 실제 크기를 인정하고, 앵커(우측·하단 zone)가
 *   유지되도록 좌표를 [0, parent - size] 로 다시 clamp 한다. Windows 11 스냅과 같은 거동이다.
 * - 부모가 min 보다도 작으면 effective min 을 부모 크기까지 낮춘다 — 스냅 결과가
 *   부모를 넘치면 `bounds='parent'` 가 드래그를 잠그므로(#78) 부모가 언제나 상한이다.
 */
export function resolveSnapBounds(
  zone: SnapZone,
  parent: BoxSize,
  min: BoxSize
): SnapBounds | null {
  const rect = zoneRect(zone, parent.width, parent.height);
  if (!rect) return null;

  const minWidth = Math.min(min.width, parent.width);
  const minHeight = Math.min(min.height, parent.height);
  const width = Math.max(rect.width, minWidth);
  const height = Math.max(rect.height, minHeight);

  return {
    x: clamp(rect.x, 0, Math.max(0, parent.width - width)),
    y: clamp(rect.y, 0, Math.max(0, parent.height - height)),
    width,
    height,
  };
}
